import apiClient from '../client';
import type {
  MessageListItem,
  MessageDetail,
  MessageFilters,
  CreateMessageRequest,
  ReplyMessageRequest,
  UnreadCountResponse,
} from '@/types/message.types';
import type { PaginatedResponse, PaginationParams } from '@/api/types/pagination.types';

export const messagesApi = {
  /**
   * Lista mensagens da caixa de entrada.
   */
  getInbox: async (params: PaginationParams): Promise<PaginatedResponse<MessageListItem>> => {
    const response = await apiClient.get<PaginatedResponse<MessageListItem>>('/v1/messages/inbox', {
      params,
    });
    return response.data;
  },

  /**
   * Lista mensagens enviadas.
   */
  getSent: async (params: PaginationParams): Promise<PaginatedResponse<MessageListItem>> => {
    const response = await apiClient.get<PaginatedResponse<MessageListItem>>('/v1/messages/sent', {
      params,
    });
    return response.data;
  },

  /**
   * Lista mensagens não lidas.
   */
  getUnread: async (params: PaginationParams): Promise<PaginatedResponse<MessageListItem>> => {
    const response = await apiClient.get<PaginatedResponse<MessageListItem>>('/v1/messages/unread', {
      params,
    });
    return response.data;
  },

  /**
   * Busca mensagens com filtros.
   */
  search: async (
    params: PaginationParams & MessageFilters
  ): Promise<PaginatedResponse<MessageListItem>> => {
    const response = await apiClient.get<PaginatedResponse<MessageListItem>>('/v1/messages', {
      params,
    });
    return response.data;
  },

  /**
   * Lista mensagens de um paciente específico.
   */
  getByPatient: async (
    patientId: string,
    params: PaginationParams
  ): Promise<PaginatedResponse<MessageListItem>> => {
    const response = await apiClient.get<PaginatedResponse<MessageListItem>>(
      `/v1/messages/patient/${patientId}`,
      { params }
    );
    return response.data;
  },

  /**
   * Retorna contagem de mensagens não lidas.
   */
  getUnreadCount: async (): Promise<UnreadCountResponse> => {
    const response = await apiClient.get<UnreadCountResponse>('/v1/messages/unread-count');
    return response.data;
  },

  /**
   * Busca mensagem por ID.
   */
  getById: async (id: string): Promise<MessageDetail> => {
    const response = await apiClient.get<MessageDetail>(`/v1/messages/${id}`);
    return response.data;
  },

  /**
   * Cria e envia uma nova mensagem.
   */
  create: async (data: CreateMessageRequest): Promise<MessageDetail> => {
    const response = await apiClient.post<MessageDetail>('/v1/messages', data);
    return response.data;
  },

  /**
   * Envia uma resposta a uma mensagem.
   */
  reply: async (id: string, data: ReplyMessageRequest): Promise<MessageDetail> => {
    const response = await apiClient.post<MessageDetail>(`/v1/messages/${id}/reply`, data);
    return response.data;
  },

  /**
   * Marca uma mensagem como lida.
   */
  markAsRead: async (id: string): Promise<void> => {
    await apiClient.post(`/v1/messages/${id}/read`);
  },

  /**
   * Marca múltiplas mensagens como lidas.
   */
  markAllAsRead: async (messageIds: string[]): Promise<void> => {
    await apiClient.post('/v1/messages/read-all', messageIds);
  },
};
